import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../../../../../../../../../hooks/auth';
import { useProduct } from '../../../../../../../../../../../../hooks/product';
import { listAppointmentsService } from '../../../../../../../../../../../../services/Appointments/ListAppointmentsService';
import { IDetailedAppointment } from '../../../../../../../../../../../../services/Appointments/ShowAppointmentsService';
import { translateError } from '../../../../../../../../../../../../utils/errors';
import {
  maskDateTime,
  maskMoney,
} from '../../../../../../../../../../../../utils/formatters/handleMask';
import {
  AppointmentTable,
  IAppointmentTableItem,
} from '../../../../../../../../components/AppointmentTable';

interface ILoadAppointmentsProps {
  userName?: string;
  page?: number;
  productId: string;
}

export const ProductAppointmentList = (): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const { product } = useProduct();

  const [productAppointmentList, setProductAppointmentList] = useState<
    IAppointmentTableItem[]
  >([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('activityName');
  const [totalPages, setTotalPages] = useState<number>();

  const loadAppointments = useCallback(
    async ({ page, userName, productId }: ILoadAppointmentsProps) => {
      try {
        const { items: appointments, pages } = await listAppointmentsService({
          page,
          userName,
          productId,
        });

        const parsedProductAppointmentLists: IAppointmentTableItem[] =
          appointments.map((appointment) => ({
            ...appointment,
            formattedCreatedAt: maskDateTime(appointment.createdAt),
            formattedTotalValue: maskMoney(appointment.totalValue),
          }));

        setTotalPages(pages);
        setProductAppointmentList(parsedProductAppointmentLists);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados das reservas, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setProductAppointmentList([]);

    if (product) {
      loadAppointments({
        ...currentFilters,
        page: currentPage,
        productId: product.id,
      });
    }
  }, [
    currentPage,
    loadAppointments,
    currentFilters,
    authenticatedVenture,
    product,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );
  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleAppointmentPayment = (
    paidAppointment: IDetailedAppointment,
  ): void => {
    setProductAppointmentList((prevState) =>
      prevState.map((productAppointment) =>
        productAppointment.id === paidAppointment.id
          ? {
              ...productAppointment,
              paymentStatus: paidAppointment.paymentStatus,
            }
          : productAppointment,
      ),
    );
  };

  const handleDelete = (productAppointment: IAppointmentTableItem): void => {
    setProductAppointmentList((prevState) =>
      prevState.filter((book) => book.id !== productAppointment.id),
    );
  };

  return (
    <AppointmentTable
      appointments={productAppointmentList}
      currentPage={currentPage}
      filterBy={filterBy}
      onChangeFilterBy={handleFilterBy}
      onDelete={handleDelete}
      onFilter={debounceHandleFilter}
      onPageChange={handlePageChange}
      onPay={handleAppointmentPayment}
      totalPages={totalPages}
    />
  );
};
